 import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  BookOpen,
  Filter,
  Save,
  X,
  AlertCircle,
  MoreVertical,
  Calendar,
  Layers,
  ChevronLeft,
  ChevronRight,
  FileText
} from 'lucide-react';
import { useData } from '../../contexts/DataContext';
import SUBJECT_API from '../../api/services/subjectApi';
import SettingsHeader from '../../ui/SettingsHeader';
import ConfirmationModal from '../../ui/ConfirmationModal';
import Loader from '../../ui/Loader';

const Subjects = () => {
  const { classes: apiClasses, currentAcademicYear } = useData();
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  
  const [showForm, setShowForm] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [subjectToDelete, setSubjectToDelete] = useState(null);
  const [editingSubject, setEditingSubject] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [formData, setFormData] = useState({
    subjectName: '',
    subjectCode: '',
    unitId: '',
    description: '',
    subjectTeacherName: '',
    academicYear: '',
    isActive: true
  });
  const [errors, setErrors] = useState({});

  // Auto-select first class if available
  useEffect(() => {
    if (apiClasses && apiClasses.length > 0 && !selectedClass) {
      setSelectedClass(apiClasses[0]._id);
    }
  }, [apiClasses, selectedClass]);

  const loadSubjects = useCallback(async () => {
    if (!selectedClass || !currentAcademicYear) {
      if(selectedClass && !currentAcademicYear) {
      }
      return;
    }
    setLoading(true);
    try {
      const response = await SUBJECT_API.getSubjectsByClass(selectedClass, currentAcademicYear);
      setSubjects(response.data || []);
    } catch (error) {
      const message = error.message || 'Failed to load subjects';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [selectedClass, currentAcademicYear]);

  useEffect(() => {
    loadSubjects();
  }, [loadSubjects]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.subjectName.trim()) newErrors.subjectName = 'Subject name is required';
    if (!formData.unitId) newErrors.unitId = 'Class selection is required';
    if (!formData.subjectCode.trim()) newErrors.subjectCode = 'Subject Code is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      setLoading(true);
      try {
        const payload = {
            ...formData,
            academicYear: currentAcademicYear // Ensure we always send the current context year
        };

        if (editingSubject) {
          const response = await SUBJECT_API.updateSubject(editingSubject._id || editingSubject.id, payload);
          if (response.success) {
            toast.success(response.message || 'Subject updated successfully');
          } else {
            toast.error(response.message || 'Failed to update subject');
            return;
          }
        } else {
          const response = await SUBJECT_API.createSubject(payload);
          if (response.success) {
            toast.success(response.message || 'Subject created successfully');
          } else {
            toast.error(response.message || 'Failed to create subject');
            return;
          }
        }

        setShowForm(false);
        setEditingSubject(null);
        resetForm();
        loadSubjects();
      } catch (error) {
        const message = error.message || 'Failed to save subject';
        toast.error(message);
        setErrors({ submit: message });
      } finally {
        setLoading(false);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      subjectName: '',
      subjectCode: '',
      unitId: selectedClass || '',
      description: '',
      subjectTeacherName: '',
      academicYear: currentAcademicYear || '',
      isActive: true
    });
    setErrors({});
  };

  const handleEdit = (subject) => {
    setEditingSubject(subject);
    setFormData({
      subjectName: subject.subjectName || '',
      subjectCode: subject.subjectCode || '',
      unitId: typeof subject.unitId === 'object' ? subject.unitId._id : subject.unitId || '',
      description: subject.description || '',
      subjectTeacherName: subject.subjectTeacherName || '',
      academicYear: subject.academicYear || currentAcademicYear,
      isActive: subject.isActive !== undefined ? subject.isActive : true
    });
    setShowForm(true);
  };

  const handleDeleteClick = (subjectId) => {
    setSubjectToDelete(subjectId);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!subjectToDelete) return;
    setIsDeleting(true);
    try {
      const response = await SUBJECT_API.deleteSubject(subjectToDelete);
      if (response.success) {
        toast.success(response.message || 'Subject deleted successfully');
        setShowDeleteModal(false);
        setSubjectToDelete(null);
        loadSubjects();
      } else {
        toast.error(response.message || 'Failed to delete subject');
      }
    } catch (error) {
      const message = error.message || 'Failed to delete subject';
      toast.error(message);
    } finally {
      setIsDeleting(false);
    }
  };

  const filteredSubjects = subjects.filter(subject => {
    const matchesSearch = subject.subjectName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (subject.subjectCode && subject.subjectCode.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesSearch;
  });

  return (
    <div className="space-y-6 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SettingsHeader
        title="Subjects Management"
        showBack={false}
        icon={BookOpen}
        buttons={[
          {
            label: "Add New Subject",
            icon: Plus,
            onClick: () => {
              resetForm();
              setShowForm(true);
            },
            variant: "default",
          },
        ]}
      >
        <div className="flex flex-col sm:flex-row gap-2">
          {/* Class Filter */}
          <div className="relative min-w-[150px]">
            <Filter className="absolute w-3.5 h-3.5 text-gray-400 transform -translate-y-1/2 left-2.5 top-1/2 pointer-events-none" />
            <select
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
              className="w-full pl-8 pr-7 py-1.5 text-xs bg-gray-50/50 border border-gray-200 rounded-lg outline-none input-field transition-all appearance-none cursor-pointer hover:bg-white"
            >
              <option value="">Select Class</option>
              {apiClasses.map((cls) => (
                <option key={cls._id} value={cls._id}>
                  {cls.unitName}
                </option>
              ))}
            </select>
            <div className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
              <ChevronRight className="w-3.5 h-3.5 rotate-90" />
            </div>
          </div>

          <div className="relative w-full sm:w-56">
            <Search className="absolute w-3.5 h-3.5 text-gray-400 transform -translate-y-1/2 left-2.5 top-1/2" />
            <input
              type="text"
              placeholder="Search subjects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full py-1.5 pl-8 pr-3 text-xs bg-gray-50/50 border border-gray-200 rounded-lg outline-none input-field transition-all hover:bg-white"
            />
          </div>
        </div>
      </SettingsHeader>

      {/* Data Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {!selectedClass ? (
            <div className="p-8 text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full mb-3" style={{ backgroundColor: 'color-mix(in srgb, var(--primary-color), white 90%)' }}>
                 <BookOpen className="h-6 w-6" style={{ color: 'var(--primary-color)' }} />
                </div>
                <h3 className="text-sm font-bold text-gray-900 mb-1">Select a Class</h3>
                <p className="text-xs text-gray-500">Please select a class from the dropdown above to view its subjects.</p>
            </div>
        ) : loading ? (
             <Loader />
        ) : filteredSubjects.length > 0 ? (
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th scope="col" className="px-3 py-2 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Code</th>
                            <th scope="col" className="px-3 py-2 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Subject Name</th>
                            <th scope="col" className="px-3 py-2 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Teacher</th>
                            <th scope="col" className="px-3 py-2 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {filteredSubjects.map((subject) => (
                            <tr key={subject._id} className="hover:bg-gray-50 transition-colors group">
                                <td className="px-3 py-2 whitespace-nowrap">
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border uppercase tracking-tighter" style={{ backgroundColor: 'color-mix(in srgb, var(--primary-color), white 95%)', color: 'var(--primary-color)', borderColor: 'color-mix(in srgb, var(--primary-color), white 80%)' }}>
                                        {subject.subjectCode}
                                    </span>
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap">
                                    <div className="text-xs font-bold text-gray-900">{subject.subjectName}</div>
                                    {subject.description && <div className="text-xs text-gray-500 truncate max-w-xs">{subject.description}</div>}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap">
                                    <div className="text-xs text-gray-600">
                                        {subject.subjectTeacherName || <span className="text-gray-400 italic">Not Assigned</span>}
                                    </div>
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-medium">
                                    <div className="flex justify-end gap-1.5 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                                        <button 
                                            onClick={() => handleEdit(subject)}
                                            className="p-1 rounded transition-colors text-gray-400 hover:text-primary-600 hover:bg-gray-100"
                                            title="Edit"
                                        >
                                            <Edit className="h-3.5 w-3.5" />
                                        </button>
                                        <button 
                                            onClick={() => handleDeleteClick(subject._id)}
                                            className="text-gray-400 hover:text-red-500 p-1 hover:bg-red-50 rounded transition-colors"
                                            title="Delete"
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        ) : (
            <div className="flex flex-col items-center justify-center py-20 px-4">
                <div className="relative mb-6">
                    <div className="absolute inset-0 bg-blue-100 rounded-full blur-2xl opacity-20 animate-pulse"></div>
                    <div className="relative w-20 h-20 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl border border-gray-200 shadow-sm flex items-center justify-center group-hover:scale-105 transition-transform duration-500">
                        <BookOpen className="h-10 w-10 text-gray-300" />
                        <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-white rounded-lg shadow-sm border border-gray-100 flex items-center justify-center">
                            <Plus className="h-3 w-3 text-gray-400" />
                        </div>
                    </div>
                </div>
                
                <h3 className="text-lg font-bold text-gray-900 mb-2">No Subjects Found</h3>
                <p className="text-sm text-gray-500 max-w-sm text-center mb-8">
                    {searchTerm 
                        ? `We couldn't find any subjects matching "${searchTerm}". Try a different search term.` 
                        : "It looks like you haven't added any subjects to this class yet. Start by creating your first subject."
                    }
                </p>

                {!searchTerm && (
                    <button
                        onClick={() => {
                            resetForm();
                            setShowForm(true);
                        }}
                        className="btn-primary flex items-center gap-2 px-6 py-2.5 shadow-lg shadow-primary-500/20 hover:shadow-primary-500/40 transition-all active:scale-95"
                    >
                        <Plus className="h-4 w-4" />
                        <span>Create Your First Subject</span>
                    </button>
                )}
                {searchTerm && (
                    <button
                        onClick={() => setSearchTerm('')}
                        className="font-bold text-xs hover:underline transition-all"
                        style={{ color: 'var(--primary-color)' }}
                    >
                        Clear Search Filter
                    </button>
                )}
            </div>
        )}
      </div>

      {/* Subject Form Modal */}
      {showForm && (
        <SubjectFormModal
          show={showForm}
          onClose={() => setShowForm(false)}
          onSubmit={handleSubmit}
          formData={formData}
          setFormData={setFormData}
          editingSubject={editingSubject}
          apiClasses={apiClasses}
          handleInputChange={handleInputChange}
          loading={loading}
          errors={errors}
          currentAcademicYear={currentAcademicYear}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title="Delete Subject"
        message="Are you sure you want to delete this subject? This action cannot be undone."
        confirmText="Delete Subject"
        isLoading={isDeleting}
      />
    </div>
  );
};

// --- Subcomponents ---

const SubjectFormModal = ({ show, onClose, onSubmit, formData, setFormData, editingSubject, apiClasses, handleInputChange, loading, errors, currentAcademicYear }) => {
    if (!show) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col animate-in fade-in zoom-in duration-200">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50/50">
              <div className="flex items-center space-x-3">
                <div className="p-2 rounded-lg" style={{ backgroundColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 10%, white)' }}>
                  <BookOpen className="h-6 w-6" style={{ color: 'var(--primary-color, #4f46e5)' }} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    {editingSubject ? 'Edit Subject' : 'Add New Subject'}
                  </h2>
                  <p className="text-sm text-gray-500">
                   {currentAcademicYear ? `Academic Year: ${currentAcademicYear}` : 'Loading Year...'}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
                <form id="subjectForm" onSubmit={onSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                            Subject Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            name="subjectName"
                            type="text"
                            value={formData.subjectName}
                            onChange={handleInputChange}
                                className={`block w-full rounded-lg border-gray-300 shadow-sm sm:text-sm py-2.5 px-3 input-field ${errors.unitId ? 'border-red-500' : ''}`}
                            placeholder="e.g., Mathematics"
                        />
                         {errors.subjectName && <p className="mt-1 text-xs text-red-500">{errors.subjectName}</p>}
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                Subject Code <span className="text-red-500">*</span>
                            </label>
                            <input
                                name="subjectCode"
                                type="text"
                                value={formData.subjectCode}
                                onChange={handleInputChange}
                                className="block w-full rounded-lg border-gray-300 shadow-sm sm:text-sm py-2.5 px-3 input-field"
                                placeholder="e.g., MATH101"
                            />
                             {errors.subjectCode && <p className="mt-1 text-xs text-red-500">{errors.subjectCode}</p>}
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                Academic Year
                            </label>
                            <input
                                type="text"
                                value={currentAcademicYear || 'Loading...'}
                                disabled
                                className="block w-full rounded-lg border-gray-200 bg-gray-50 text-gray-500 shadow-sm sm:text-sm py-2.5 px-3 cursor-not-allowed"
                            />
                        </div>

                         <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                Class / Unit <span className="text-red-500">*</span>
                            </label>
                            <select
                                name="unitId"
                                value={formData.unitId}
                                onChange={handleInputChange}
                                className={`block w-full rounded-lg border-gray-300 shadow-sm sm:text-sm py-2.5 px-3 input-field ${errors.unitId ? 'border-red-500' : ''}`}
                            >
                                <option value="">Select Class</option>
                                {apiClasses.map(cls => (
                                <option key={cls._id} value={cls._id}>
                                    {cls.unitName}
                                </option>
                                ))}
                            </select>
                            {errors.unitId && <p className="mt-1 text-xs text-red-500">{errors.unitId}</p>}
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                Teacher Name
                            </label>
                            <input
                                name="subjectTeacherName"
                                type="text"
                                value={formData.subjectTeacherName}
                                onChange={handleInputChange}
                                className="block w-full rounded-lg border-gray-300 shadow-sm sm:text-sm py-2.5 px-3 input-field"
                                placeholder="e.g., Mr. Sharma"
                            />
                        </div>
                    </div>

                    {errors.submit && (
                        <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm flex items-center">
                            <AlertCircle className="h-4 w-4 mr-2" />
                            {errors.submit}
                        </div>
                    )}
                </form>
            </div>

            <div className="p-6 border-t border-gray-200 bg-gray-50/50 flex justify-end gap-3">
                 <button
                    type="button"
                    onClick={onClose}
                    className="px-5 py-2.5 rounded-lg text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    form="subjectForm"
                    disabled={loading}
                    className="btn-primary flex items-center"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {loading ? 'Saving...' : (editingSubject ? 'Update Subject' : 'Create Subject')}
                  </button>
            </div>
          </div>
        </div>
    );
};

export default Subjects;
